/**
 * Centralized logging utility for the Nuclear Monitor application.
 * Provides different log levels and environment-aware logging.
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

const LEVEL_NAMES = {
  0: 'ERROR',
  1: 'WARN',
  2: 'INFO',
  3: 'DEBUG',
};

// Helper functions
function getLogLevel() {
  const envLevel = process.env.REACT_APP_LOG_LEVEL || 'INFO';
  return LOG_LEVELS[envLevel.toUpperCase()] || LOG_LEVELS.INFO;
}

function formatMessage(level, message, ...args) {
  const timestamp = new Date().toISOString();
  const levelName = LEVEL_NAMES[level];
  return [`[${timestamp}] ${levelName}:`, message, ...args];
}

class Logger {
  constructor() {
    // Set log level based on environment
    this.logLevel = getLogLevel();
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  log(level, message, ...args) {
    if (level <= this.logLevel) {
      const formattedArgs = formatMessage(level, message, ...args);

      switch (level) {
        case LOG_LEVELS.ERROR:
          // eslint-disable-next-line no-console
          console.error(...formattedArgs);
          break;
        case LOG_LEVELS.WARN:
          // eslint-disable-next-line no-console
          console.warn(...formattedArgs);
          break;
        case LOG_LEVELS.INFO:
          // eslint-disable-next-line no-console
          console.info(...formattedArgs);
          break;
        case LOG_LEVELS.DEBUG:
          if (this.isDevelopment) {
            // eslint-disable-next-line no-console
            console.log(...formattedArgs);
          }
          break;
        default:
          // eslint-disable-next-line no-console
          console.log(...formattedArgs);
      }
    }
  }

  error(message, ...args) {
    this.log(LOG_LEVELS.ERROR, message, ...args);
  }

  warn(message, ...args) {
    this.log(LOG_LEVELS.WARN, message, ...args);
  }

  info(message, ...args) {
    this.log(LOG_LEVELS.INFO, message, ...args);
  }

  debug(message, ...args) {
    this.log(LOG_LEVELS.DEBUG, message, ...args);
  }
}

// Create a singleton instance
const logger = new Logger();

export default logger;
